import React, { useState } from "react";
import propTypes from "prop-types";

import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/styles";
import moment from "moment";

import { getHourMinute } from "utils/utils";
import { InputErrorStrings, valueStrings } from "constants/constantStrings";
import { DATE_TIME_SECONDS_FORMAT, REPORT_CUSTOM_MAX_DAYS } from "constants/globalConstants";
import { SYSTEMS_PAGES } from "constants/pages.constant";

import DupontButton from "components/DupontButton";
import ErrorLabel from "components/ErrorLabel";
import { inputBoxStyles } from "components/InputBox/input.style";

import CalenderIcon from "../../assets/SvgIcon/Calender";

import { DatePickerConfig, MAX_YEARS_IN_PICKER, TimePickerConfig } from "./DateTimeConfig";
import DupontDatePicker from "./DupontDatePicker";

import "bootstrap/dist/css/bootstrap-grid.min.css";
import "./index.scss";

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
    minWidth: 0,
    "&:hover": {
      backgroundColor: "transparent"
    }
  }
}));

export default function DatePopover({ startDate, endDate, setSelectedStartEndDates, page, popoverAnchor, setPopoverAnchor, disableIcon, maxDays }) {
  const checkCurrentActivePage = targetPage => page && page.toLowerCase() === targetPage.toLowerCase();
  const classes = inputBoxStyles(this);
  const iconClasses = useStyles();
  const maxMonths = maxDays / 30; // Converting to months

  const getStartDate = () => {
    if (startDate) {
      const dates = new Date(Number(startDate));
      return dates;
    } else {
      const calendarDate = new Date();
      calendarDate.setHours(0, 0, 0, 0);
      return calendarDate;
    }
  };
  const [calendarStartDate, setStartDate] = useState(getStartDate());
  const [calendarEndDate, setEndDate] = useState(endDate ? new Date(Number(endDate)) : new Date());
  const [anchorEl, setAnchorEl] = useState(checkCurrentActivePage("Baseline") ? null : popoverAnchor);
  const [isInline] = useState(setSelectedStartEndDates ? false : true);
  const [maxDate, setMaxDate] = useState(new Date());

  const handleClose = () => {
    setAnchorEl(null);
    if (checkCurrentActivePage(SYSTEMS_PAGES.CHARTS)) {
      setPopoverAnchor(null);
    }
  };

  const checkTimeSelection = date => {
    const currentTime = getHourMinute(new Date());
    const timeSelected = date && getHourMinute(date) !== currentTime && (date.getHours() !== 0 || date.getMinutes() !== 0);
    return timeSelected;
  };

  const onEndDateChange = date => {
    if (!date) {
      setEndDate(null);
      return;
    }
    if (!checkTimeSelection(date)) {
      date.setHours(23, 59, 59, 0);
    }
    setEndDate(date);
  };

  const handleClick = event => setAnchorEl(event.currentTarget);

  const onStartDateChange = date => {
    setStartDate(date);
    if (moment(calendarEndDate).diff(moment(date), "days") > maxDays || date > calendarEndDate) {
      setEndDate(null);
    }
    const dates = [moment(new Date()), moment(new Date(moment(date).add(maxDays, "days")))];
    setMaxDate(new Date(moment.min(dates)));
  };

  const onStartTimeChange = time => {
    if (time) {
      setStartDate(time);
    } else {
      calendarStartDate.setHours(0, 0, 0);
    }
  };

  const onEndTimeChange = time => {
    if (time === null) {
      calendarEndDate.setHours(0, 0, 0);
    } else {
      setEndDate(time);
    }
  };

  const handleApply = () => {
    if (typeof setSelectedStartEndDates === "function") {
      const startDate = moment(calendarStartDate, DATE_TIME_SECONDS_FORMAT, moment.defaultFormat);
      const endDate = moment(calendarEndDate, DATE_TIME_SECONDS_FORMAT, moment.defaultFormat);
      setSelectedStartEndDates({ startDate, endDate });
    }
    setAnchorEl(null);
    if (checkCurrentActivePage(SYSTEMS_PAGES.CHARTS)) {
      setPopoverAnchor(null);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      {(checkCurrentActivePage("Baseline") || checkCurrentActivePage(SYSTEMS_PAGES.REPORTS)) && (
        <Button aria-describedby={id} onClick={handleClick} disabled={disableIcon} classes={{ root: iconClasses.root }}>
          <CalenderIcon disabled={disableIcon} />
        </Button>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        <div>
          <div className={!isInline ? "dateContainer startEndDateContainer marginTop10 left-right-margin-10" : "marginTop10"}>
            <div>
              <DupontDatePicker
                selected={calendarStartDate}
                onChange={onStartDateChange}
                startDate={calendarStartDate}
                maxDate={new Date()}
                minDate={new Date().setFullYear(new Date().getFullYear() - MAX_YEARS_IN_PICKER)}
                className={`date-time-input-width`}
                open
                label={valueStrings.startDate}
                labelClass="marginBtm10 dateFont"
                {...DatePickerConfig}
              />
              {!calendarStartDate && <ErrorLabel message={InputErrorStrings.required} />}
            </div>
            <DupontDatePicker
              selected={calendarStartDate}
              className={`margin-left-10 date-time-input-width`}
              onChange={onStartTimeChange}
              labelClass="marginBtm10 dateFont margin-left-10"
              open
              label={valueStrings.startTime}
              {...TimePickerConfig}
            />

            <div className="dateFont leftRightMargin8 and-text">{valueStrings.and}</div>
            <div>
              <DupontDatePicker
                selected={calendarEndDate}
                onChange={onEndDateChange}
                startDate={calendarStartDate}
                endDate={calendarEndDate}
                minDate={calendarStartDate}
                maxDate={maxDate}
                openToDate={calendarStartDate}
                className={`date-time-input-width`}
                open
                label={valueStrings.endDate}
                labelClass="marginBtm10 dateFont"
                {...DatePickerConfig}
              />
              {!calendarEndDate && <ErrorLabel message={InputErrorStrings.required} />}
            </div>
            <DupontDatePicker
              onChange={onEndTimeChange}
              className={`${classes.textfield} custom-input margin-left-10 date-time-input-width `}
              selected={calendarEndDate}
              label={valueStrings.endTime}
              labelClass="marginBtm10 dateFont margin-left-10"
              open
              {...TimePickerConfig}
            />
          </div>
          <div className="note-button-container">
            <div className="descriptionText note-text">{valueStrings.dateNote.replace("MAX_MONTHS", maxMonths)}</div>
            <div className="button-container">
              <DupontButton customClass="margin-left-20 cancel-button" type="secondary" onClick={handleClose}>
                {valueStrings.cancel}
              </DupontButton>
              <DupontButton disabled={!calendarEndDate || !calendarStartDate} customClass="leftRightMargin8" onClick={handleApply}>
                {valueStrings.apply}
              </DupontButton>
            </div>
          </div>
        </div>
      </Popover>
    </div>
  );
}

DatePopover.propTypes = {
  maxDays: propTypes.number,
  timeEnabled: propTypes.bool
};

DatePopover.defaultProps = {
  maxDays: REPORT_CUSTOM_MAX_DAYS,
  timeEnabled: true
};
